import { HOST_API } from 'src/config-global';

import type { AxiosRequestConfig } from 'axios';
import axios from 'axios';

// ----------------------------------------------------------------------
const axiosInstance = axios.create({ baseURL: HOST_API });

export default axiosInstance;

// ----------------------------------------------------------------------

export const fetcher = async (args: string | [string, AxiosRequestConfig]) => {
  const [url, config] = Array.isArray(args) ? args : [args];

  const res = await axiosInstance.get(url, { ...config });

  return res.data;
};

// ----------------------------------------------------------------------

export const endpoints = {
  auth: {
    login: '/auth/login',
    getLoginOptions: '/auth/getLoginOptions',
    register: '/auth/register', //register email only
    connectWithToken: (token: string) => `/auth/connect/${token}`,
    setupAccount: '/auth/setupAccount', //finalize registration
    refreshToken: '/auth/refreshToken',
    revokeRefreshToken: '/auth/revokeRefreshToken',
    checkUserToken: '/auth/checkUserToken',
    sendEmailToReset: '/auth/submitEmailForPasswordReset',
    verifyPasswordTokenValidity: '/auth/verifyPasswordTokenValidity',
    updatePasswordWithToken: '/auth/updatePasswordWithToken',
    googleLogin: '/auth/google',
    googleDisconnect: '/auth/google/disconnect',
    appleLogin: '/auth/apple',
  },
  users: {
    root: (id?: number) => `/user/${id || ''}`,
    available: '/user/available',
    filteredList: (filter: string) => `/user?filter=${filter}`,
    me: {
      root: '/user/me',
      language: '/user/me/language',
      changePassword: '/user/me/changePassword',
      deleteAccount: '/user/me/deleteAccount',
    },
    deactivate: (id: number) => `/user/deactivate/${id}`,
    deactivateGroup: '/user/deactivate-group',
    reactivate: (id: number) => `/user/reactivate/${id}`,
    invite: (id: number) => `/user/${id}/invite`,
    folderAccess: (id: number) => `/user/${id}/folder-access`,
    inviteGroup: '/user/invite-group',
    documents: (id: number) => `/user/${id}/documents`,
  },
  organization: '/organization',
  folder: {
    root: (id?: number) => `/folder/${id ?? ''}`,
    linkFiles: (folderId: number) => `/folder/${folderId}/files/link`,
    removeFile: (folderId: number, fileId: number) =>
      `/folder/${folderId}/files/delete/${fileId}`,
    regenerateAcls: '/folder/regenerate-acls',
  },
  equipment: {
    root: (id?: number) => `/equipments/${id || ''}`,
    list: '/equipments/list',
    lookup: '/equipments/lookup',
    histories: '/equipments/history',
    inspect: {
      root: (id: number) => `/equipments/${id}/inspect`,
      multiple: '/equipments/inspect',
    },
    allocate: (id: number) => `/equipments/${id}/allocate`,
    updateDeadlines: (id: number) => `/equipments/deadlines/update/${id}`,
    bulk: '/equipments/bulk', // bulk operations
    locations: {
      fleetMap: '/equipments/locations',
      root: (id: number) => `/equipments/${id}/locations`,
    },
  },
  model: {
    root: (id?: number) => `/models/${id || ''}`,
    search: '/models/search',
  },
  brand: {
    root: (id?: number) => `/brands/${id || ''}`,
  },
  category: {
    root: '/categories',
  },
  transfer: {
    root: (id?: number) => `/transfer/${id || ''}`,
    validate: (token: string) => `/transfer/public/validate/${token}`,
    check: (externalId: string) => `/transfer/public/check/${externalId}`,
    respond: '/transfer/public/respondToTransfer',
    certificate: (id: number) => `/transfer/${id}/certificate`,
  },
  tag: {
    root: (id?: number) => `/tags/${id || ''}`,
  },
  training: {
    root: (id?: number) => `/training/${id || ''}`,
    user: (userId: number) => `/training/user/${userId}`,
  },
  files: '/files',
  fileImport: '/files/import',
  images: '/images',
  stats: {
    root: '/stats',
    dashboard: '/stats/dashboard',
  },
  import: {
    root: '/import',
    models: '/import/models',
    equipments: '/import/equipments',
    customModels: '/import/customModels',
  },
  export: {
    root: '/export',
    users: '/export/users',
  },
  billing: {
    info: '/billing/info',
    manageSubscription: '/billing/manageSubscription',
    synchronizeWithStripe: 'billing/synchronizeWithStripe',
  },
  events: {
    root: '/events',
    user: (id: number) => `/events/user/${id}`,
  },
  admin: {
    organizations: '/admin/organizations',
    organization: (id: number) => `/admin/organizations/${id}`,
    studio: '/admin/studio',
    users: '/admin/users',
    user: (id: number) => `/admin/users/${id}`,
    impersonate: (id: number) => `/admin/users/${id}/impersonate`,
    mails: '/admin/mails',
    brands: '/admin/brands',
    brandApprove: (id: number) => `/admin/brands/${id}/approve`,
    brandReject: (id: number) => `/admin/brands/${id}/reject`,
    actions: {
      recalculateUserCompliance: '/admin/actions/recalculate-user-compliance',
      recalculateTrainingStatuses:
        '/admin/actions/recalculate-training-statuses',
      reindexModels: '/admin/actions/reindex-models',
      checkEquipmentStatusChanges:
        '/admin/actions/check-equipment-status-changes',
      job: (id: string) => `/admin/actions/jobs/${id}`,
    },
    userImport: {
      template: '/admin/user-import/template',
      analyze: '/admin/user-import/analyze',
      execute: '/admin/user-import/execute',
      job: (id: string) => `/admin/user-import/jobs/${id}`,
    },
    billing: {
      clients: '/admin/billing/clients',
      client: (id: number) => `/admin/billing/clients/${id}`,
      clientInvoices: (id: number) => `/admin/billing/clients/${id}/invoices`,
      invoiceVoid: (id: number) => `/admin/billing/invoices/${id}/void`,
      invoiceResend: (id: number) => `/admin/billing/invoices/${id}/resend`,
    },
  },
  distributor: {
    dashboard: '/distributor/dashboard',
    organizations: '/distributor/organizations',
    organization: (publicId: string) =>
      `/distributor/organizations/${publicId}`,
    equipments: '/distributor/equipments',
    referencesAnalytics: '/distributor/references/analytics',
    users: '/distributor/users',
    user: (publicId: string) => `/distributor/users/${publicId}`,
    impersonate: (publicId: string) =>
      `/distributor/users/${publicId}/impersonate`,
  },
  allowances: '/allowances',
  inspectionChecklist: {
    root: (id?: number) => `/inspection-checklists/${id ?? ''}`,
  },
  inspection: {
    root: (id?: number) => `/inspection/${id ?? ''}`,
  },
  repair: {
    root: (id?: number) => `/repair/${id ?? ''}`,
  },
  shortlink: {
    public: (id: string) => `/shortlink/public/${id}`,
    private: (id: string) => `/shortlink/private/${id}`,
  },
};
